"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import {HLSVideoPlayer} from "@/components/ui/SanityVideoPlayer";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import person1 from "@/public/animatedAssets/person1.png";

type State = "collapsed" | "expanded";

export const AnimatedAsset9 = ({
  className = "",
}: {
  className?: string;
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const setState = async (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;

    if (next === "expanded" && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }

    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .75};

  const rollLeft = {
    collapsed: {rotate: 0, x: 0, transition: ease},
    expanded: {rotate: -7, x: "-1.5rem", transition: ease},
  }

  const rollRight = {
    collapsed: {rotate: 0, x: 0, transition: ease},
    expanded: {rotate: 7, x: "1.5rem", transition: ease},
  }

  const videoFade = {
    collapsed: {opacity: 0, transition: {ease: "circOut", duration: 1.2}},
    expanded: {opacity: 1, transition: {duration: 0}},
  }

  const confettiVideo = {
    embed: false,
    muxVideo: {
      playbackId: "yo3dUZGlsqjizmlKaydl1u88Z6zbcg7TW83h9yOiWYY",
      assetId: "",
      filename: "",
      status: ""
    },
    embedUrl: "",
    thumbnail: null
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] bg-[#0A1A1D] flex flex-col items-center justify-center p-[2rem]",
        "relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="bg-[#0A1A1D] absolute left-0 top-0 !w-full !h-full object-cover mix-blend-screen"
        variants={videoFade}
        animate={controls}
        initial="collapsed"
      >
        <HLSVideoPlayer
          ref={videoRef}
          source={confettiVideo}
          autoPlay={false}
          muted
          playsInline
          className="!w-full !h-full object-cover"
        />
      </motion.div>

      <div className="flex flex-row-reverse items-center justify-center relative">
        <motion.div
          className="relative w-[12rem] h-[12rem] rounded-full border-[2px] border-[#D9F9B9]/20 bg-[#0A1A1D] flex items-center justify-center"
          variants={rollRight}
          animate={controls}
          initial="collapsed"
        >
          <div className="w-[10.5rem] h-[10.5rem] rounded-full bg-[#112628] flex items-center justify-center">
            <p className="mb-0 leading-none text-[5rem] font-medium font-funnel text-[#D9F9B9]">1st</p>
          </div>
        </motion.div>

        <motion.div
          className="-mr-[4rem] w-[12rem] relative h-[12rem] rounded-full border-[2px] border-[#D9F9B9] bg-[#0A1A1D] flex items-center justify-center"
          variants={rollLeft}
          animate={controls}
          initial="collapsed"
        >
          <Image
            className="!w-[10.5rem] !h-[10.5rem] object-cover rounded-full"
            src={person1}
            quality={100}
            alt="portrait"
          />
        </motion.div>
      </div>
    </div>
  );
};
